import React from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Chip, Divider, useTheme } from '@mui/material';
import kpiDescriptions from '../../data/kpiDescriptions.json';

export interface BenchmarkData {
  median: number;
  p25: number;
  p75: number;
  p90: number;
  industry?: string;
  nonNullCount?: number;
}

export interface KPIData {
  id: string;
  title: string;
  value: number | string;
  unit?: string;
  formatType?: 'percentage' | 'currency' | 'number' | 'ratio';
  benchmark?: BenchmarkData;
  calculation?: string;
  description?: string;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: number;
  category: 'financial' | 'operational' | 'market' | 'risk' | 'business_model';
  visualType?: 'gauge' | 'progress' | 'simple';
  target?: number;
  isGoodHigh?: boolean; // true if higher values are better
  yearLabel?: string;
}

interface KPICardProps {
  kpi: KPIData;
  compact?: boolean;
  showDescription?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({ kpi, compact = false, showDescription = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const formatValue = (value: number | string, formatType?: string, unit?: string): string => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return 'N/A';

    switch (formatType) {
      case 'percentage':
        return `${(value * 100).toFixed(1)}%`;
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      case 'ratio':
        return `${value.toFixed(2)}x`;
      case 'number':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }).format(value);
      default:
        return `${value}${unit ? ` ${unit}` : ''}`;
    }
  };

  const getPerformanceColor = (value: number, benchmark?: BenchmarkData, isGoodHigh = true): string => {
    if (!benchmark || typeof value !== 'number') return theme.palette.text.primary;

    const { median, p75, p25 } = benchmark;

    // Colors adapted for light/dark mode readability
    const colors = isDark
      ? { good: '#56D364', mid: '#79C0FF', warn: '#E3B341', bad: '#FF7B76' }
      : { good: '#3F5D66', mid: '#4E6694', warn: '#BF8700', bad: '#FF564F' };

    if (isGoodHigh) {
      if (value >= p75) return colors.good;
      if (value >= median) return colors.mid;
      if (value >= p25) return colors.warn;
      return colors.bad;
    } else {
      if (value <= p25) return colors.good;
      if (value <= median) return colors.mid;
      if (value <= p75) return colors.warn;
      return colors.bad;
    }
  };

  const getPerformanceLabel = (value: number, benchmark?: BenchmarkData, isGoodHigh = true): string => {
    if (!benchmark || typeof value !== 'number') return '';

    const { median, p75, p25 } = benchmark;

    if (isGoodHigh) {
      if (value >= p75) return 'Top Quartile';
      if (value >= median) return '2nd Quartile';
      if (value >= p25) return '3rd Quartile';
      return 'Low Quartile';
    } else {
      if (value <= p25) return 'Top Quartile';
      if (value <= median) return '2nd Quartile';
      if (value <= p75) return '3rd Quartile';
      return 'Low Quartile';
    }
  };

  const renderVisualization = () => {
    const numericValue = typeof kpi.value === 'number' ? kpi.value : 0;

    // If no benchmark, show large centered value
    if (!kpi.benchmark && typeof kpi.value === 'number') {
      return (
        <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
          <Typography variant="h4" fontWeight="bold" color="text.primary" align="center" sx={{ fontSize: '2rem' }}>
            {formatValue(kpi.value, kpi.formatType, kpi.unit)}
          </Typography>
        </Box>
      );
    }

    // Always use progress bar visualization when benchmark is available
    if (
      (kpi.visualType === 'gauge' || kpi.visualType === 'progress') &&
      kpi.benchmark &&
      typeof kpi.value === 'number'
    ) {
      // The bar represents the middle 50% (P25 to P75)
      // The bar is always 100% filled to show this range
      // Calculate position for the actual value indicator
      const range = kpi.benchmark.p75 - kpi.benchmark.p25;
      const normalizedValue = ((numericValue - kpi.benchmark.p25) / range) * 100;

      // Clamp position to visual boundaries (with margins for callout)
      const valuePosition = Math.max(10, Math.min(90, normalizedValue));

      // Check if value is outside the middle 50%
      const isOutsideRange = numericValue < kpi.benchmark.p25 || numericValue > kpi.benchmark.p75;

      return (
        <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={1}>
          {/* Large value display - moved above the bar */}
          <Typography
            variant="h5"
            fontWeight="bold"
            color={getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh)}
            align="center"
            sx={{ fontSize: '1.5rem', mb: 0.5 }}
          >
            {formatValue(kpi.value, kpi.formatType, kpi.unit)}
          </Typography>

          <Box position="relative" mx={2}>
            {/* The bar itself - always 100% to show middle 50% range */}
            <Box
              sx={{
                height: 8,
                borderRadius: 0,
                backgroundColor: getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh),
                opacity: 0.3,
                border: '1px solid',
                borderColor: getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh),
              }}
            />

            {/* Position indicator on the bar (only if within range) */}
            {!isOutsideRange && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${valuePosition}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '3px',
                  height: '16px',
                  backgroundColor: getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh),
                }}
              />
            )}

            {/* Outside range indicators */}
            {numericValue < kpi.benchmark.p25 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderRight: `6px solid ${getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh)}`,
                }}
              />
            )}
            {numericValue > kpi.benchmark.p75 && (
              <Box
                sx={{
                  position: 'absolute',
                  right: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderLeft: `6px solid ${getPerformanceColor(numericValue, kpi.benchmark, kpi.isGoodHigh)}`,
                }}
              />
            )}
          </Box>

          {/* Quartile labels below the bar */}
          <Box display="flex" justifyContent="space-between" px={2}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              P25: {formatValue(kpi.benchmark.p25, kpi.formatType, kpi.unit)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              P75: {formatValue(kpi.benchmark.p75, kpi.formatType, kpi.unit)}
            </Typography>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const performanceColor = getPerformanceColor(
    typeof kpi.value === 'number' ? kpi.value : 0,
    kpi.benchmark,
    kpi.isGoodHigh
  );
  const performanceLabel = getPerformanceLabel(
    typeof kpi.value === 'number' ? kpi.value : 0,
    kpi.benchmark,
    kpi.isGoodHigh
  );

  return (
    <Card
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        borderRadius: 0,
        border: '1px solid',
        borderColor: isDark ? '#30363D' : '#E1E4E8',
        bgcolor: isDark ? '#161B22' : 'background.paper',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          transform: 'translateY(-2px)',
          borderColor: isDark ? '#484F58' : '#A8B1BD',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          pb: showDescription ? 2 : 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 1.5,
        }}
      >
        {/* Row 1: Name and Graph (includes value) */}
        <Box sx={{ minHeight: compact ? 'auto' : 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with title */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Typography
              variant={compact ? 'body2' : 'h6'}
              fontWeight="medium"
              sx={{ flex: 1, lineHeight: 1.2, fontSize: compact ? '0.8rem' : '1rem' }}
            >
              {kpi.title}
            </Typography>
            {kpi.yearLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap', ml: 1, mt: 0.2, opacity: 0.7 }}
              >
                {kpi.yearLabel}
              </Typography>
            )}
          </Box>

          {/* Visualization (includes value display) */}
          {renderVisualization()}
        </Box>

        {/* Row 2: Benchmark */}
        <Box
          sx={{
            minHeight: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 0.75,
          }}
        >
          {/* Performance Badge */}
          {performanceLabel && kpi.benchmark && (
            <Box display="flex" justifyContent="center">
              <Chip
                label={performanceLabel}
                size="small"
                sx={{
                  backgroundColor: performanceColor,
                  color: isDark ? '#0D1117' : 'white',
                  fontWeight: 'medium',
                  fontSize: '0.7rem',
                  height: '22px',
                }}
              />
            </Box>
          )}

          {/* Benchmark Info */}
          {!compact && kpi.benchmark && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
                sx={{ mb: 0.5, display: 'block' }}
              >
                Industry Benchmarks:
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Median: {formatValue(kpi.benchmark.median, kpi.formatType, kpi.unit)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Top 25%: {formatValue(kpi.benchmark.p75, kpi.formatType, kpi.unit)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Row 4: Description (if enabled) */}
        {showDescription && kpiDescriptions[kpi.id as keyof typeof kpiDescriptions] && !compact && (
          <Box sx={{ flexGrow: 1, mt: 'auto', borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
            {/* Sample Size */}
            {kpi.benchmark?.nonNullCount && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  <strong>Sample Size:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  N = {kpi.benchmark.nonNullCount}
                </Typography>
              </>
            )}
            {/* Calculation */}
            {kpiDescriptions[kpi.id as keyof typeof kpiDescriptions].calculation && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>
                  <strong>Calculation:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {kpiDescriptions[kpi.id as keyof typeof kpiDescriptions].calculation}
                </Typography>
              </>
            )}
            {/* Description */}
            <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>
              <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {kpiDescriptions[kpi.id as keyof typeof kpiDescriptions].description}
            </Typography>
            {/* Removed Interpretation section */}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
